// CareLogix™ — Gate 3: Membership / Account Selection (Phase 32.2)
// Final constitutional gate. User picks their intelligence tier intent.
// Storage key: carelogix_membership_intent_v1
// Tiers (canonical from /api/membership/tiers): free / professional / executive / enterprise
import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { PALETTE } from "@/components/animation/MotionShared";

export const MEMBERSHIP_INTENT_KEY = "carelogix_membership_intent_v1";

export function getMembershipIntent() {
  if (typeof window === "undefined") return null;
  try { return window.localStorage.getItem(MEMBERSHIP_INTENT_KEY); }
  catch { return null; }
}

export function setMembershipIntent(intent) {
  if (typeof window === "undefined") return;
  try {
    if (intent) window.localStorage.setItem(MEMBERSHIP_INTENT_KEY, intent);
    else        window.localStorage.removeItem(MEMBERSHIP_INTENT_KEY);
  } catch (_) { /* ignore */ }
  try { window.dispatchEvent(new CustomEvent("carelogix:membership-intent-changed", { detail: { value: intent } })); } catch (_) { /* ignore */ }
}

// CONSTITUTIONAL MONETIZATION RULE (Message 264) —
// All bullets below MUST map to operational backend routes / UI surfaces.
// No unbuilt features are advertised. External integrations that are mocked
// are explicitly labeled MOCKED_AWAITING_KEYS in operator views, not here.
const CONSUMER_TIERS = [
  {
    id: "guest",
    label: "Free Preview",
    badge: "Guest",
    tier_class: "Consumer Intelligence",
    price_text: "$0",
    price_sub: "no account required",
    headline: "Explore CareLogix ORACLE™ with public, advisory-only intelligence.",
    bullets: [
      "Public ORACLE 7-mode console",
      "Signal Engine™ snapshots (LOW severity)",
      "Event Intelligence™ timeline (read-only)",
      "Compliance Lock™ on every output",
    ],
    cta: "Continue as Guest",
    accent: PALETTE.slate3,
  },
  {
    id: "professional",
    label: "Professional",
    badge: "Most popular",
    tier_class: "Consumer Intelligence",
    price_text: "$49",
    price_sub: "per month",
    headline: "Daily personalized market intelligence with LOGIX-2™ briefings.",
    bullets: [
      "Saved Watchlists + LOGIX-2™ briefings",
      "MEDIUM/HIGH severity signals",
      "ORACLE Predictive Command™ access",
      "Standard Intelligence Dossier (PDF)",
    ],
    cta: "Choose Professional",
    accent: PALETTE.greenAccent,
  },
];

const CAPITAL_TIERS = [
  {
    id: "executive",
    label: "Executive",
    badge: "Premium",
    tier_class: "Capital Intelligence",
    price_text: "$199",
    price_sub: "per month",
    headline: "Executive intelligence with Agent Swarm reasoning and Capital Command access.",
    bullets: [
      "Everything in Professional",
      "EXTREME severity signals",
      "ORACLE Global Capital Command™ (16th domain)",
      "Funding Command Center™ + Executive Dossier v3",
    ],
    cta: "Choose Executive",
    accent: PALETTE.gold,
  },
  {
    id: "enterprise",
    label: "Enterprise",
    badge: "For Organizations",
    tier_class: "Capital Intelligence",
    price_text: "$999",
    price_sub: "per month",
    headline: "Organization-wide governance with Operator Command Center and audit access.",
    bullets: [
      "Everything in Executive",
      "Operator Command Center access",
      "Enterprise Audit Orchestration + governance widgets",
      "CSV / JSON audit export endpoints",
    ],
    cta: "Talk to Enterprise",
    accent: PALETTE.goldBright,
  },
];

const TIERS = [...CONSUMER_TIERS, ...CAPITAL_TIERS];

export default function MembershipSelectionGate({ onAccept, testid = "membership-selection-gate" }) {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(null);

  const handlePick = (tierId) => {
    setSubmitting(tierId);
    setMembershipIntent(tierId);
    if (typeof onAccept === "function") {
      try { onAccept(tierId); } catch (_) { /* ignore */ }
    }
    // Route by intent:
    //   guest        → enter ORACLE in guest mode (gate orchestrator continues to ORACLE app)
    //   pro/exec/ent → push the user to /access (sign in / create account) so they can claim that tier
    //   existing     → sent to /access via the "already a member" CTA below (handled separately)
    setTimeout(() => {
      if (tierId === "guest") {
        // Stay; orchestrator will render ORACLE
        setSubmitting(null);
        return;
      }
      try { navigate("/access"); } catch (_) { /* fallback */ if (typeof window !== "undefined") window.location.href = "/access"; }
    }, 320);
  };

  const handleExistingAccount = () => {
    // Persist a neutral "existing" intent so the gate doesn't replay on reload
    setMembershipIntent("existing");
    try { navigate("/access"); } catch (_) { if (typeof window !== "undefined") window.location.href = "/access"; }
  };

  return (
    <main style={S.shell} data-testid={testid} role="dialog" aria-modal="true" aria-labelledby={`${testid}-title`}>
      <div style={S.bgGlow} aria-hidden="true" />
      <motion.section
        style={S.card}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        <span style={S.gateChip} data-testid={`${testid}-step`}>GATE 3 OF 3</span>

        <header style={S.header}>
          <span style={S.eyebrow}>MEMBERSHIP CONTINUITY™</span>
          <h1 style={S.title} id={`${testid}-title`} data-testid={`${testid}-title`}>
            Choose Your Intelligence Tier
          </h1>
          <p style={S.subtitle} data-testid={`${testid}-subtitle`}>
            Pick the membership that fits your decision-making style. You can change this any time.
          </p>
        </header>

        <ul style={S.tierGrid} data-testid={`${testid}-tier-grid`}>
          {TIERS.map((t) => (
            <li key={t.id} style={{ ...S.tier, borderColor: t.accent }} data-testid={`${testid}-tier-${t.id}`}>
              <header style={S.tierHead}>
                <span style={S.tierClass} data-testid={`${testid}-tier-class-${t.id}`}>
                  {t.tier_class}
                </span>
                <span style={{ ...S.tierBadge, color: t.accent, borderColor: t.accent }}>{t.badge}</span>
                <h3 style={{ ...S.tierLabel, color: t.accent }}>{t.label}</h3>
                <div style={S.tierPriceRow}>
                  <span style={S.tierPrice}>{t.price_text}</span>
                  <span style={S.tierPriceSub}>{t.price_sub}</span>
                </div>
              </header>
              <p style={S.tierHeadline}>{t.headline}</p>
              <ul style={S.tierList}>
                {t.bullets.map((b) => (
                  <li key={b} style={S.tierItem}>
                    <span style={{ ...S.tierBullet, color: t.accent }} aria-hidden="true">✓</span>
                    {b}
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => handlePick(t.id)}
                disabled={submitting === t.id}
                style={{
                  ...S.tierBtn,
                  borderColor: t.accent,
                  color: t.id === "professional" || t.id === "executive" || t.id === "enterprise" ? "#0b0f14" : t.accent,
                  background: t.id === "guest" ? "transparent" : `linear-gradient(180deg, ${t.accent} 0%, ${t.accent}cc 100%)`,
                }}
                data-testid={`${testid}-pick-${t.id}`}
              >
                {submitting === t.id ? "Loading…" : t.cta}
              </button>
            </li>
          ))}
        </ul>

        <div style={S.pledge} data-testid={`${testid}-truthfulness-pledge`}>
          <strong style={{ color: PALETTE.goldBright }}>Truthfulness Pledge:</strong>{" "}
          Only operational capabilities are listed. Stubs and pending integrations are labeled in
          operator views. No autonomous execution at any tier.
        </div>

        <div style={S.existingRow}>
          <span style={S.existingPrompt}>Already a CareLogix member?</span>
          <button
            type="button"
            onClick={handleExistingAccount}
            style={S.existingBtn}
            data-testid={`${testid}-existing-account`}
          >
            Sign in to your account →
          </button>
        </div>

        <footer style={S.footer}>
          <span style={S.footerLink}>Pricing Details</span>
          <span style={S.footerSep}>|</span>
          <span style={S.footerLink}>Compare Tiers</span>
          <span style={S.footerSep}>|</span>
          <span style={S.footerLink}>Privacy Policy</span>
        </footer>

        <div style={S.guard}>
          <span aria-hidden="true">📦</span>
          <span>Continuity-first packaging · Cancel any time · No autonomous execution at any tier.</span>
        </div>
      </motion.section>
    </main>
  );
}

const S = {
  shell:    { position: "fixed", inset: 0, zIndex: 9990, background: "radial-gradient(circle at 50% 0%, rgba(15,45,30,0.55) 0%, rgba(4,6,8,1) 65%)", overflowY: "auto", padding: "24px 16px 48px", display: "flex", alignItems: "flex-start", justifyContent: "center", fontFamily: "'Figtree', 'Inter', system-ui, sans-serif" },
  bgGlow:   { position: "absolute", inset: 0, backgroundImage: "radial-gradient(ellipse 700px 360px at 50% 6%, rgba(197,160,89,0.10), transparent 65%)", pointerEvents: "none" },
  card:     { position: "relative", width: "100%", maxWidth: 1080, background: "linear-gradient(180deg, rgba(11,15,20,0.96), rgba(6,18,12,0.96))", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 24, padding: "28px clamp(20px, 4vw, 44px) 24px", color: PALETTE.slate3, display: "flex", flexDirection: "column", gap: 14, boxShadow: "0 30px 80px rgba(0,0,0,0.55), inset 0 0 60px rgba(197,160,89,0.05)" },
  gateChip: { alignSelf: "center", color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.32em", padding: "4px 10px", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 999, background: "rgba(197,160,89,0.06)" },
  header:   { display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 6 },
  eyebrow:  { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.32em" },
  title:    { margin: 0, color: PALETTE.goldBright, fontSize: "clamp(22px, 4vw, 34px)", letterSpacing: "0.06em", fontWeight: 800 },
  subtitle: { margin: 0, color: PALETTE.slate3, fontSize: "clamp(13px, 1.6vw, 15px)", maxWidth: 640, lineHeight: 1.55 },
  tierGrid: { margin: 0, padding: 0, listStyle: "none", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 },
  tier:     { background: "rgba(4,6,8,0.85)", border: "1px solid", borderRadius: 14, padding: 14, display: "flex", flexDirection: "column", gap: 10 },
  tierHead: { display: "flex", flexDirection: "column", gap: 4 },
  tierClass:{ alignSelf: "flex-start", color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: "0.18em", textTransform: "uppercase", opacity: 0.85, marginBottom: 2 },
  tierBadge:{ alignSelf: "flex-start", fontFamily: "'Space Mono', monospace", fontSize: 9.5, letterSpacing: "0.14em", padding: "3px 8px", borderRadius: 999, border: "1px solid", background: "rgba(255,255,255,0.02)" },
  tierLabel:{ margin: 0, fontSize: 18, fontWeight: 800, letterSpacing: "0.04em" },
  tierPriceRow: { display: "flex", alignItems: "baseline", gap: 8, marginTop: 4 },
  tierPrice: { color: PALETTE.goldBright, fontSize: 22, fontWeight: 800, fontFamily: "'Space Mono', monospace" },
  tierPriceSub: { color: PALETTE.slate4, fontSize: 11, letterSpacing: "0.06em" },
  tierHeadline: { margin: 0, color: PALETTE.slate3, fontSize: 12.5, lineHeight: 1.5 },
  tierList: { margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 5 },
  tierItem: { display: "flex", alignItems: "flex-start", gap: 7, fontSize: 12, lineHeight: 1.4, color: PALETTE.slate3 },
  tierBullet:{ fontFamily: "'Space Mono', monospace", fontSize: 12, width: 12, textAlign: "center" },
  tierBtn:  { appearance: "none", marginTop: "auto", border: "1px solid", padding: "9px 14px", borderRadius: 999, fontWeight: 800, fontFamily: "'Space Mono', monospace", letterSpacing: "0.08em", fontSize: 11.5, cursor: "pointer" },
  existingRow: { display: "flex", flexWrap: "wrap", justifyContent: "center", alignItems: "center", gap: 10, paddingTop: 4 },
  existingPrompt: { color: PALETTE.slate4, fontSize: 12.5 },
  existingBtn: { appearance: "none", background: "transparent", color: PALETTE.gold, border: `1px dashed ${PALETTE.goldGlow}`, padding: "6px 14px", borderRadius: 999, fontFamily: "'Space Mono', monospace", fontSize: 11.5, letterSpacing: "0.08em", cursor: "pointer" },
  footer:   { marginTop: 4, display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 10, color: PALETTE.slate4, fontSize: 11.5 },
  footerLink:{ textDecoration: "underline dotted", cursor: "pointer", color: PALETTE.gold },
  footerSep:{ color: "rgba(255,255,255,0.18)" },
  pledge:   { marginTop: 6, padding: "10px 14px", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 12, color: PALETTE.slate3, fontSize: 12, lineHeight: 1.5, textAlign: "center", background: "rgba(197,160,89,0.05)" },
  guard:    { display: "flex", justifyContent: "center", alignItems: "center", gap: 8, color: PALETTE.slate5, fontSize: 10.5, textAlign: "center" },
};
